import * as React from 'react';
import { css } from '@emotion/core';
import { FLEX_COLUMN, FLEX_CENTERED, FLEX_ROW } from '../../../../layout/flexBox';
import ProgressCircle from '../../../../loading/ProgressCircle';
import Metric from '../../../../layout/Metric';
import { Str } from '../../../../localization/translationKeys';
import { Requirement } from '../../../models';
import { sumCompleted, mapRingConfig, getSurveyLevelColor } from './module';
import { CHART_COLORS, COLORS } from '../../../../globalStyle/colors';
import LocalDiv from '../../../../localization/LocalDiv';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCheck, faCircle } from '@fortawesome/free-solid-svg-icons';
import BulletedMsg from '../../../../layout/BulletedMsg';
import { useAppState } from '../../../../core/redux/hooks';
import { isRtl } from '../../../../localization/module';


const styles = {
  root: [
    FLEX_COLUMN,
    css`
      justify-content: flex-start;
    `,
  ],

  circle:     css`
    width: 210px; 
    height: 210px; 
    margin: 0 auto;
  `,
  metric:     css`
    margin-bottom: 6px;
  `,
  metricValue: css`
    font-size: 33px;
  `,
  metricLabel: css`
    font-size: 12px;
  `,
  metricIcon: css`
    font-size: 20px; 
    margin-top: 7px; 
    margin-left: 7px;
  `,
};


const mkCountMsg = (
  isRtl: boolean,
  count: number,
  msg: string,
): string => isRtl ? `${msg} ${count}` : `${count} ${msg}`;


type Props = {
  requirements: Requirement[] | undefined;
  circleDiameterPx?: number;
  className?: string;
};


/**
 * RaterSummaryChart: a component that renders the number of completed surveys including a doughnut chart showing the
 * proportions of completed surveys from specific rater "levels" (groups).
 *
 * PureComponent: true
 * Redux Connected: false
 */
const RaterSummaryChart: React.FC<Props> = props => {
  const rtl = useAppState(state => isRtl(state.locale.textDirection));

  const requirements = React.useMemo(
    () => props.requirements
            ?.filter(req => req.completedCount > 0)
            .sort((a, b) => a.completedCount - b.completedCount)
          ?? [],
    [props.requirements],
  );

  const numCompleted = React.useMemo(() => sumCompleted(requirements), [requirements]);
  const chartConfig  = React.useMemo(() => mapRingConfig(numCompleted, requirements), [numCompleted, requirements]);

  return (
    <div css={styles.root} className={props.className}>
      <ProgressCircle
        ringConfig={chartConfig}
        radius={props.circleDiameterPx! / 2}
        stroke={props.circleDiameterPx! / 4}
        css={styles.circle}
      >
        <Metric
          value={numCompleted}
          label={Str.Completed}
          defaultLabel='Completed'
          valueStyle={styles.metricValue}
          labelStyle={styles.metricLabel}
          css={[
            FLEX_COLUMN,
            FLEX_CENTERED,
            styles.metric,
            css`color: ${numCompleted > 0 ? COLORS.BLUE : CHART_COLORS.RING_BG};`,
          ]}
        >
          <FontAwesomeIcon icon={faUserCheck} css={styles.metricIcon} />
        </Metric>
      </ProgressCircle>
      {
        numCompleted > 0 && (
          <LocalDiv css={[FLEX_ROW, FLEX_CENTERED, css`flex-wrap: wrap; margin-top: 20px;`]}>
            {
              requirements?.map((req, i) => (
                <BulletedMsg
                  key={req.surveyLevelId}
                  icon={faCircle}
                  msg={mkCountMsg(rtl, req.completedCount, req.fullName)}
                  iconStyle={css`color: ${getSurveyLevelColor(i)}`}
                  css={css`margin: 5px; color: ${COLORS.ICON_GREY};`}
                />
              ))
            }
          </LocalDiv>
        )
      }
    </div>
  );
};

RaterSummaryChart.defaultProps = {
  circleDiameterPx: 210,
};

export default RaterSummaryChart;
